/**
 * LeafRow: one leaf in the column beside the page (Figma 00, 22:153): its dot, its name, its status badge
 * (APPROVED, REVIEWING, DRAFT), and, on the leaf being read, what opens under it (the edit box, the kind, the
 * verdicts) on the amber ground. The row's header presses to open the leaf; the row is an article named by its
 * label, so a screen and a test find it by its place.
 */
import type { ReactNode } from 'react';
import { Button } from 'react-aria-components';
import { SPACE, TEXT, WALK, WASH_ALPHA, WEIGHT, tint, usePalette } from '../theme';
import { focusRing } from './action-button';
import { Chip, toneOf } from './chip';

export type LeafTone = 'approved' | 'reviewing' | 'draft';

export function LeafRow({
  tone,
  label,
  name,
  badge,
  onOpen,
  children,
}: {
  tone: LeafTone;
  /** The row's accessible name: the leaf's place. */
  label: string;
  /** The name the screen prints. */
  name: ReactNode;
  /** The status word, in capitals. */
  badge: string;
  /** Opens the leaf, when the leaf is not the one open. */
  onOpen?: () => void;
  /** What stands open under the header: only on the leaf being read. */
  children?: ReactNode;
}) {
  const p = usePalette();
  const dot = tone === 'approved' ? p.categorical[2] : tone === 'reviewing' ? p.absence : p.blue;
  const badgeTone = tone === 'approved' ? 'approved' : tone === 'reviewing' ? 'waiting' : 'draft';
  const header = (
    <>
      <span aria-hidden="true" style={{ width: WALK.dot, height: WALK.dot, borderRadius: '50%', background: dot, flexShrink: 0 }} />
      <span style={{ fontSize: TEXT.control, fontWeight: WEIGHT.medium, color: p.ink, textAlign: 'left', lineHeight: 'normal' }}>{name}</span>
      <span style={{ flex: 1 }} />
      <Chip look={toneOf(badgeTone)} size="row">
        {badge}
      </Chip>
    </>
  );
  const headerStyle = { display: 'flex', alignItems: 'center', gap: SPACE.sm, width: '100%' } as const;
  return (
    <div
      role="article"
      aria-label={label}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: SPACE.sm,
        padding: `${SPACE.snug}px ${SPACE.lg}px`,
        borderBottom: `1px solid ${p.hairline}`,
        background: tone === 'reviewing' ? tint(p.absence, WASH_ALPHA.whisper) : 'transparent',
      }}
    >
      {onOpen === undefined ? (
        <div style={headerStyle}>{header}</div>
      ) : (
        <Button
          onPress={onOpen}
          style={(s) => ({
            ...headerStyle,
            padding: 0,
            border: 'none',
            background: 'transparent',
            font: 'inherit',
            cursor: 'pointer',
            outline: 'none',
            boxShadow: s.isFocusVisible ? focusRing(p) : undefined,
          })}
        >
          {header}
        </Button>
      )}
      {children}
    </div>
  );
}
